import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FileObject } from './../../../core/interfaces/file-object';
import { CameraCaptureService } from './../../../core/services/camera-capture.service';
import { preparePdfPages } from './../../../core/pdf-pages/with-pdf-pages';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss']
})
export class FileUploadComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput', { static: false }) fileInput?: ElementRef;

  @Input() fileObjects: FileObject[] = [];
  @Input() title: string = '';
  @Input() allowMultiple = false;
  @Input() showGallery = true;
  @Input() shouldRefresh = false;
  @Input() cameraEnabled = false;
  @Input() pasteEnabled = true;
  @Input() enableEditing = false;
  @Input() allowedFileTypes = 'image/*, application/pdf;';
  @Input() hintMessage: string = '';
  @Input() previewEnabled = false;

  @Output() filesEdited = new EventEmitter<FileObject[]>();
  @Output() fileUpload = new EventEmitter<FileObject[]>();
  @Output() fileDelete = new EventEmitter<FileObject>();

  fileHover = false;
  contextMenuVisible = false;
  contextMenuPosition = { x: 0, y: 0 };

  // Variables to support preview with rotation.
  previewImage: string | null = null;
  rotation: number = 0;
  private originalFile?: File;

  /** Renders a PDF's pages in the background as soon as it is added, so saving does not wait for them. */
  preparePdfPages: (file: File) => Promise<void> = (file) => preparePdfPages(file);

  constructor(private cameraCaptureService: CameraCaptureService, private host?: ElementRef<HTMLElement>) { }

  // One bound handler, so the same function is removed again; a fresh bind each time left every closed box listening.
  private readonly pasteListener = (event: ClipboardEvent) => this.onPaste(event);

  ngOnInit() {
    document.addEventListener('paste', this.pasteListener);
  }

  ngOnDestroy() {
    document.removeEventListener('paste', this.pasteListener);
  }

  triggerPaste() {
    navigator.permissions.query({ name: 'clipboard-read' as PermissionName })
      .then(result => {
        if (result.state === 'granted' || result.state === 'prompt') {
          this.readClipboard();
        } else {
          console.warn('Clipboard read permission denied');
        }
      }).catch(err => {
        console.error('Failed to query clipboard-read permission: ', err);
      });
  }

  readClipboard() {
    navigator.clipboard.read().then((clipboardItems) => {
      const files: File[] = [];
      for (const clipboardItem of clipboardItems) {
        for (const type of clipboardItem.types) {
          if (this.isFileAllowed({ type } as File)) {
            clipboardItem.getType(type).then((blob) => {
              const file = new File([blob], `pasted-file.${blob.type.split('/')[1]}`, { type: blob.type });
              files.push(file);
              if (files.length === clipboardItems.length) {
                this.handleFileUpload(files);
              }
            });
          }
        }
      }
    }).catch(err => {
      console.error('Failed to read clipboard contents: ', err);
    });
  }

  onPaste(event: ClipboardEvent) {
    if (!this.pasteEnabled || !this.takesPaste()) return;
    const clipboardItems = Array.from(event.clipboardData?.items || []);
    const files: File[] = clipboardItems
      .filter(item => item.kind === 'file')
      .map(item => item.getAsFile() as File)
      .filter(file => this.isFileAllowed(file));
    if (files.length) {
      this.handleFileUpload(files);
    }
  }

  /**
   * With a dialog open that has its own upload box, a paste belongs there and not to the page behind it (a note's
   * media box under the note scan dialog). Only the topmost dialog with a box takes it.
   */
  private takesPaste(): boolean {
    const el = this.host?.nativeElement;
    if (!el) return true;
    const panes = Array.from(document.querySelectorAll('.cdk-overlay-container .cdk-overlay-pane'))
      .filter((pane) => pane.querySelector('app-file-upload'));
    const top = panes[panes.length - 1];
    return !top || top.contains(el);
  }

  isFileAllowed(file: File): boolean {
    const allowedTypesArray = this.allowedFileTypes.split(',').map(type => type.trim());
    return allowedTypesArray.some(allowedType => {
      if (allowedType.includes('/*')) {
        const baseType = allowedType.split('/')[0];
        return file.type.startsWith(baseType + '/');
      } else {
        return file.type === allowedType;
      }
    });
  }

  onFileUpload(event: any) {
    const files = Array.from(event.target.files) as File[];
    if (this.previewEnabled && files.length > 0 && files[0].type.startsWith('image/')) {
      this.handlePreviewFile(files[0]);
    } else {
      this.handleFileUpload(files);
    }
    const fileInputElem = this.fileInput!.nativeElement;
    fileInputElem.setAttribute('accept', this.allowedFileTypes);
  }

  onFileDrop(event: any) {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files) as File[];
    const validFiles = this.filterValidFiles(files);
    // When preview is enabled and the dropped file is an image, enter preview mode.
    if (this.previewEnabled && validFiles.length > 0 && validFiles[0].type.startsWith('image/')) {
      this.handlePreviewFile(validFiles[0]);
    } else {
      this.handleFileUpload(validFiles);
    }
  }

  filterValidFiles(files: File[]): File[] {
    const allowedTypesArray = this.allowedFileTypes.split(',').map(type => type.trim());
    return files.filter(file => {
      return allowedTypesArray.some(allowedType => {
        if (allowedType.includes('/*')) {
          const baseType = allowedType.split('/')[0];
          return file.type.startsWith(baseType + '/');
        } else {
          return file.type === allowedType;
        }
      });
    });
  }

  onFileDragOver(event: any) {
    event.preventDefault();
  }

  /**
   * For non-preview mode, handle file uploads as usual.
   */
  handleFileUpload(files: File[]) {
    // In memory only: the pages never show in the container; the save picks them up.
    files.filter((file) => file?.type === 'application/pdf')
      .forEach((file) => this.preparePdfPages(file).catch(() => undefined));
    if (this.allowMultiple) {
      const newObjects = files.map(file => ({
        url: URL.createObjectURL(file),
        type: file.type,
        name: file.name,
        uploaded: false
      }));
      newObjects.forEach(obj => {
        if (!this.fileObjects.some(fileObj => fileObj.url === obj.url)) {
          this.fileObjects.push(obj);
        }
      });
      this.fileUpload.emit(this.fileObjects);
    } else {
      const obj = {
        url: URL.createObjectURL(files[0]),
        type: files[0].type,
        name: files[0].name,
        uploaded: false
      };
      if (!this.fileObjects.some(fileObj => fileObj.url === obj.url)) {
        this.fileObjects = [obj];
        this.fileUpload.emit([this.fileObjects[0]]);
      }
    }
    if (this.shouldRefresh) {
      this.fileObjects = [];
    }
  }

  /**
   * When preview is enabled, load an image for preview.
   */
  handlePreviewFile(file: File) {
    this.originalFile = file;
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.previewImage = e.target.result;
      this.rotation = 0;
    };
    reader.readAsDataURL(file);
  }

  removeFile(index: number) {
    const file: FileObject = this.fileObjects[index];
    this.fileObjects.splice(index, 1);
    this.fileDelete.emit(file);
  }

  handleTransformedFiles(editedFiles: FileObject[]) {
    this.fileObjects = editedFiles;
    this.filesEdited.emit(editedFiles);
  }

  async triggerCamera() {
    try {
      const file = await this.cameraCaptureService.capturePhoto(
        this.cameraCaptureService.isNative() ? undefined : this.fileInput?.nativeElement
      );
      if (!file) return;

      // Restore accept after the service set it to image/* for the camera flow.
      if (this.fileInput?.nativeElement) {
        this.fileInput.nativeElement.setAttribute('accept', this.allowedFileTypes);
        this.fileInput.nativeElement.removeAttribute('capture');
      }

      if (this.previewEnabled && file.type.startsWith('image/')) {
        this.handlePreviewFile(file);
      } else {
        this.handleFileUpload([file]);
      }
    } catch (err) {
      console.error('Camera capture failed', err);
    }
  }

  // --- Preview & Rotation Methods ---

  rotateLeft(ev: MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    this.rotation = (this.rotation - 90) % 360;
  }

  rotateRight(ev: MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    this.rotation = (this.rotation + 90) % 360;
  }

  retakeImage(ev: MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    this.previewImage = null;
    this.rotation = 0;
    this.originalFile = undefined;
  }

  /**
   * Confirm the preview image by rotating it using a canvas,
   * then create a new File and emit it.
   */
  confirmImage(ev: MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    if (!this.originalFile || !this.previewImage) return;
    this.getRotatedImageBlob(this.previewImage, this.rotation).then(blob => {
      const rotatedFile = new File([blob], this.originalFile!.name, { type: blob.type });
      const fileObj = {
        url: URL.createObjectURL(rotatedFile),
        type: rotatedFile.type,
        name: rotatedFile.name,
        uploaded: false
      };
      this.fileObjects = [fileObj];
      this.fileUpload.emit([fileObj]);
      // Reset preview variables.
      this.previewImage = null;
      this.rotation = 0;
      this.originalFile = undefined;
    }).catch(err => {
      console.error('Error processing rotated image', err);
    });
  }

  /**
   * Uses a canvas to rotate the image according to the current rotation.
   */
  getRotatedImageBlob(dataUrl: string, rotation: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return reject(new Error('Failed to get canvas context'));
        }
        const angle = rotation % 360;
        let width = image.width;
        let height = image.height;

        // For rotations of 90° or 270°, swap the canvas dimensions.
        if (Math.abs(angle) === 90 || Math.abs(angle) === 270) {
          canvas.width = height;
          canvas.height = width;
        } else {
          canvas.width = width;
          canvas.height = height;
        }

        // Rotate the canvas context.
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((angle * Math.PI) / 180);
        ctx.drawImage(image, -width / 2, -height / 2);

        // Convert the canvas content to a Blob.
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Canvas conversion failed'));
          }
        }, this.originalFile ? this.originalFile.type : 'image/jpeg');
      };
      image.onerror = (err) => reject(err);
      image.src = dataUrl;
    });
  }
}
